import { Link } from "react-router-dom";
import { formatMoney } from "@/lib/currency";

/**
 * OpportunitiesAndBills — the bottom row of the dashboard:
 *   • left/wide  = optimizer insights (top 4)
 *   • right/thin = upcoming bills (next 30 days)
 * Extracted from Dashboard.jsx in iter-31.
 */
export default function OpportunitiesAndBills({ insights, upcomingBills, currency }) {
  const cur = currency;
  const bills = upcomingBills || [];

  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 card-flat p-6 animate-fadeup">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-eyebrow">Top opportunities</div>
            <div className="text-base font-medium mt-1">
              Save up to{" "}
              <span className="num-md text-[var(--accent-positive)]">
                {formatMoney(insights?.total_monthly_savings || 0, cur)}
              </span>
              /mo
            </div>
          </div>
          <Link to="/optimizer" className="btn-ghost" data-testid="dashboard-see-all-opt">See all</Link>
        </div>
        {(!insights?.insights || insights.insights.length === 0) ? (
          <div className="text-sm text-[var(--text-muted)]">
            You&apos;re optimized. No suggestions right now.
          </div>
        ) : (
          <div className="space-y-3">
            {insights.insights.slice(0, 4).map(i => (
              <div
                key={i.id}
                className="p-4 rounded-xl border border-[var(--border-default)] hover:border-[var(--accent-primary)] transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`badge ${i.severity === "high" ? "badge-danger" : i.severity === "medium" ? "badge-warn" : "badge-neutral"}`}>
                        {i.severity}
                      </span>
                      <div className="font-medium">{i.title}</div>
                    </div>
                    <div className="text-sm text-[var(--text-secondary)] mt-1.5">{i.detail}</div>
                  </div>
                  {i.monthly_savings > 0 && (
                    <div className="text-right whitespace-nowrap">
                      <div className="num-md text-[var(--accent-positive)]">
                        {formatMoney(i.monthly_savings, cur)}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">
                        per month
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card-flat p-6 animate-fadeup">
        <div className="label-eyebrow">Upcoming bills</div>
        <div className="text-base font-medium mt-1">Next 30 days</div>
        {bills.length === 0 ? (
          <div className="text-sm text-[var(--text-muted)] mt-6">No bills due soon.</div>
        ) : (
          <div className="mt-4 divide-y divide-[var(--border-default)]">
            {bills.map(b => (
              <div key={b.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{b.name}</div>
                  <div className="text-xs text-[var(--text-muted)] mt-0.5">Due {b.due_date}</div>
                </div>
                <div className="num-md">{formatMoney(b.amount, cur)}</div>
              </div>
            ))}
          </div>
        )}
        <Link to="/bills" className="btn-ghost w-full mt-4 justify-center inline-flex" data-testid="dashboard-manage-bills-btn">
          Manage bills
        </Link>
      </div>
    </div>
  );
}
